import { WS_BASE_URL } from "../config/constants";

class NativeWebSocketService {
  constructor() {
    this.ws = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
  }

  connect(token, onConnect, onError) {
    if (this.isConnected) return;

    try {
      // Use browser's native WebSocket with fallback support
      const wsProtocol = WS_BASE_URL.startsWith("https") ? "wss" : "ws";
      const wsHost = WS_BASE_URL.replace(/^https?:\/\//, "").replace(/\/+$/, "");
      
      // Try /ws or /ws/websocket depending on reconnect count
      const path = this.reconnectAttempts % 2 === 1 ? "/ws/websocket" : "/ws";
      this.ws = new WebSocket(`${wsProtocol}://${wsHost}${path}`);

      this.ws.onopen = () => {
        // console.log("✅ WebSocket connected as Admin");
        this.isConnected = true;
        this.reconnectAttempts = 0;

        // Send authentication token
        if (token) {
          this.ws.send(
            JSON.stringify({
              type: "AUTH",
              token: token,
            })
          );
        }

        if (onConnect) onConnect();
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (error) {
          console.error("Error parsing WebSocket message:", error);
        }
      };

      this.ws.onerror = (error) => {
        this.isConnected = false;
        if (onError) onError(error);
      };

      this.ws.onclose = () => {
        this.isConnected = false;
        this.attemptReconnect(token, onConnect, onError);
      };
    } catch (error) {
      if (onError) onError(error);
    }
  }

  handleMessage(message) {
    if (message.type === "NEW_ORDER") {
      this.handleNewOrderNotification(message.data);
    } else if (message.type === "ORDER_UPDATE") {
      this.handleOrderUpdateNotification(message.data);
    } else if (message.type === "CONNECTED") {
      console.log(" WebSocket connection confirmed");
    }
  }

  getAudioContext() {
    try {
      if (!this.audioCtx) {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (AudioContextClass) {
          this.audioCtx = new AudioContextClass();
        }
      }
      return this.audioCtx;
    } catch (e) {
      return null;
    }
  }

  playNotificationSound() {
    try {
      const ctx = this.getAudioContext();
      if (!ctx) return;

      const triggerChime = () => {
        try {
          const now = ctx.currentTime;
          const playTone = (freq, startTime, duration) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = "sine";
            osc.frequency.setValueAtTime(freq, startTime);
            gain.gain.setValueAtTime(0.35, startTime);
            gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start(startTime);
            osc.stop(startTime + duration);
          };

          playTone(587.33, now, 0.28);          // D5
          playTone(880.0, now + 0.14, 0.45);    // A5 luxury chime
        } catch (err) {
          console.warn("Error rendering notification chime:", err);
        }
      };

      if (ctx.state === "suspended") {
        ctx.resume().then(() => {
          triggerChime();
        }).catch((err) => {
          console.warn("Could not resume AudioContext:", err);
        });
      } else {
        triggerChime();
      }
    } catch (err) {
      console.warn("Could not play notification sound:", err);
    }
  }

  handleNewOrderNotification(notification) {
    this.playNotificationSound();

    const event = new CustomEvent("newOrderNotification", {
      detail: notification,
    });
    window.dispatchEvent(event);

    this.showBrowserNotification(
      "🆕 New Order Received!",
      `Order #${notification.orderId} from ${notification.customerName} - ₹${notification.totalAmount}`
    );
  }

  handleOrderUpdateNotification(notification) {
    const event = new CustomEvent("orderUpdateNotification", {
      detail: notification,
    });
    window.dispatchEvent(event);

    this.showBrowserNotification(
      "📝 Order Updated",
      `Order #${notification.orderId} status: ${notification.status}`
    );
  }

  attemptReconnect(token, onConnect, onError) {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      // console.log(
      //   `🔄 Attempting to reconnect... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`
      // );

      setTimeout(() => {
        this.connect(token, onConnect, onError);
      }, 3000 * this.reconnectAttempts);
    }
  }

  showBrowserNotification(title, body) {
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification(title, {
        body,
        icon: "/favicon.ico",
      });
    }
  }

  requestNotificationPermission() {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().then((permission) => {
        // console.log("Notification permission:", permission);
      });
    }
  }

  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
      this.isConnected = false;
    }
  }

  getConnectionStatus() {
    return this.isConnected;
  }
}

export default new NativeWebSocketService();
